import { ThemeEvents } from '@theme/events';

const CARD_SELECTOR = '[data-townwine-inventory-card]';
const PRODUCT_SELECTOR = '[data-townwine-inventory-product]';
const DEFAULT_PROXY_BASE = '/apps/townwine-follow';
const DEFAULT_MAX_PER_PERSON = 6;

const inventorySnapshotStore = new Map();
let refreshTimer = 0;
let domObserver;

function normalizeProxyBase(value) {
  const trimmedValue = String(value || '').trim();

  if (trimmedValue === '') {
    return DEFAULT_PROXY_BASE;
  }

  return trimmedValue.endsWith('/') ? trimmedValue.slice(0, -1) : trimmedValue;
}

function parseVariantIds(value) {
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatCount(value) {
  return new Intl.NumberFormat('ko-KR').format(Number(value) || 0);
}

function getProgressPercent(snapshot) {
  return Math.max(0, Math.min(100, Number(snapshot?.progressPercent) || 0));
}

function getRemainingLabel(snapshot) {
  if (!snapshot) {
    return '재고 정보 확인 중';
  }

  if (!snapshot.inventoryTracked) {
    return snapshot.availableForSale ? '재고 확인 중' : '공구 마감';
  }

  if (snapshot.remainingInventory <= 0) {
    return '재고 소진';
  }

  return `재고 ${formatCount(snapshot.remainingInventory)}병 남음`;
}

function getCardSummary(snapshot) {
  if (!snapshot) {
    return '판매 수량 확인 중';
  }

  if (!snapshot.inventoryTracked) {
    return `${formatCount(snapshot.soldCount)}병 판매`;
  }

  if (snapshot.totalInventory <= 0 && snapshot.soldCount > 0) {
    return `${formatCount(snapshot.soldCount)}병 판매 완료`;
  }

  if (snapshot.totalInventory <= 0) {
    return '판매 수량 확인 중';
  }

  return `${formatCount(snapshot.soldCount)} / ${formatCount(snapshot.totalInventory)}병 판매`;
}

function getProductBadge(snapshot) {
  if (!snapshot?.inventoryTracked) {
    return '재고 연동 중';
  }

  if (snapshot.totalInventory <= 0 && snapshot.soldCount > 0) {
    return '전량 판매 완료';
  }

  return `판매 ${formatCount(getProgressPercent(snapshot))}%`;
}

function getProductHelper(snapshot) {
  if (!snapshot?.inventoryTracked) {
    return '재고 연동 준비 중';
  }

  if (snapshot.remainingInventory <= 0) {
    return '전량 판매 완료';
  }

  return '현재 판매 가능';
}

function setText(scope, selector, value) {
  scope.querySelectorAll(selector).forEach((element) => {
    if (element instanceof HTMLElement) {
      element.textContent = value;
    }
  });
}

function syncProgressClasses(element, progressPercent) {
  element.classList.toggle('pg--ur', progressPercent >= 75 && progressPercent < 100);
  element.classList.toggle('pg--ok', progressPercent >= 100);
}

function getSnapshotForVariantId(variantId) {
  return inventorySnapshotStore.get(String(variantId || '').trim()) || null;
}

function updateCardWidget(widget, snapshot) {
  if (!(widget instanceof HTMLElement) || !snapshot) {
    return;
  }

  const progressPercent = getProgressPercent(snapshot);
  const progressBar = widget.querySelector('[data-inventory-progress-bar]');

  if (progressBar instanceof HTMLElement) {
    progressBar.style.width = `${progressPercent}%`;
  }

  syncProgressClasses(widget, progressPercent);
  setText(widget, '[data-inventory-summary]', getCardSummary(snapshot));
  setText(widget, '[data-inventory-caption]', getRemainingLabel(snapshot));
}

function updatePurchaseLimit(widget, snapshot) {
  const quantityInput = widget.querySelector('input[name="quantity"]');
  const quantityHint = widget.querySelector('[data-inventory-purchase-limit]');

  if (!(quantityInput instanceof HTMLInputElement)) {
    return;
  }

  const perPersonLimit = Number(widget.dataset.maxPerPerson || DEFAULT_MAX_PER_PERSON);
  let maxQuantity = perPersonLimit;

  if (snapshot?.inventoryTracked && snapshot.inventoryPolicy === 'DENY') {
    const remainingInventory = Number(snapshot.remainingInventory);
    const safeRemainingInventory = Number.isFinite(remainingInventory)
      ? remainingInventory
      : perPersonLimit;

    maxQuantity = Math.max(1, Math.min(perPersonLimit, safeRemainingInventory));
  }

  quantityInput.max = String(maxQuantity);

  if (Number(quantityInput.value || 1) > maxQuantity) {
    quantityInput.value = String(maxQuantity);
  }

  if (quantityHint instanceof HTMLElement) {
    quantityHint.textContent = `1인 최대 ${formatCount(maxQuantity)}병 가능`;
  }
}

function updateProductWidget(widget, snapshot) {
  if (!(widget instanceof HTMLElement) || !snapshot) {
    return;
  }

  const progressPercent = getProgressPercent(snapshot);
  const progressContainer = widget.querySelector('[data-inventory-progress-container]');
  const progressBar = widget.querySelector('[data-inventory-progress-bar]');

  if (progressContainer instanceof HTMLElement) {
    syncProgressClasses(progressContainer, progressPercent);
  }

  if (progressBar instanceof HTMLElement) {
    progressBar.style.width = `${progressPercent}%`;
  }

  widget.dataset.selectedVariantId = snapshot.legacyVariantId || widget.dataset.selectedVariantId || '';

  setText(widget, '[data-inventory-badge]', getProductBadge(snapshot));
  setText(widget, '[data-inventory-sold]', formatCount(snapshot.soldCount));
  setText(widget, '[data-inventory-total]', formatCount(snapshot.totalInventory));
  setText(widget, '[data-inventory-remaining]', formatCount(snapshot.remainingInventory));
  setText(widget, '[data-inventory-helper]', getProductHelper(snapshot));

  updatePurchaseLimit(widget, snapshot);
}

function bindProductWidget(widget) {
  if (!(widget instanceof HTMLElement) || widget.dataset.inventoryBound === 'true') {
    return;
  }

  widget.dataset.inventoryBound = 'true';
  widget.addEventListener('change', (event) => {
    const target = event.target;

    if (!(target instanceof HTMLInputElement) || target.name !== 'id') {
      return;
    }

    widget.dataset.selectedVariantId = target.value;
    updateProductWidget(widget, getSnapshotForVariantId(target.value));
  });
}

function collectInventoryGroups() {
  const groups = new Map();

  document.querySelectorAll(CARD_SELECTOR).forEach((widget) => {
    if (!(widget instanceof HTMLElement)) {
      return;
    }

    const variantId = String(widget.dataset.variantId || '').trim();

    if (!variantId) {
      return;
    }

    const proxyBase = normalizeProxyBase(widget.dataset.proxyBase);
    const group = groups.get(proxyBase) || {
      cards: [],
      products: [],
      variantIds: new Set(),
    };

    group.cards.push(widget);
    group.variantIds.add(variantId);
    groups.set(proxyBase, group);
  });

  document.querySelectorAll(PRODUCT_SELECTOR).forEach((widget) => {
    if (!(widget instanceof HTMLElement)) {
      return;
    }

    bindProductWidget(widget);

    const proxyBase = normalizeProxyBase(widget.dataset.proxyBase);
    const group = groups.get(proxyBase) || {
      cards: [],
      products: [],
      variantIds: new Set(),
    };

    group.products.push(widget);
    parseVariantIds(widget.dataset.variantIds).forEach((variantId) => {
      group.variantIds.add(variantId);
    });
    groups.set(proxyBase, group);
  });

  return groups;
}

async function fetchInventorySnapshots(proxyBase, variantIds) {
  const url = new URL(`${proxyBase}/inventory`, window.location.origin);

  variantIds.forEach((variantId) => {
    url.searchParams.append('variantId', variantId);
  });

  const response = await fetch(url.toString(), {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Inventory request failed with status ${response.status}`);
  }

  const payload = await response.json();
  const snapshots = payload?.snapshots && typeof payload.snapshots === 'object' ? payload.snapshots : {};

  Object.entries(snapshots).forEach(([key, snapshot]) => {
    inventorySnapshotStore.set(key, snapshot);
  });
}

async function hydrateInventory() {
  const groups = collectInventoryGroups();

  if (!groups.size) {
    return;
  }

  await Promise.all(
    Array.from(groups.entries()).map(async ([proxyBase, group]) => {
      const variantIds = Array.from(group.variantIds);

      if (!variantIds.length) {
        return;
      }

      try {
        await fetchInventorySnapshots(proxyBase, variantIds);
      } catch (error) {
        console.error('[townwine-inventory] failed to fetch inventory snapshots', {
          proxyBase,
          variantIds,
          message: error instanceof Error ? error.message : String(error),
        });
        return;
      }

      group.cards.forEach((widget) => {
        updateCardWidget(widget, getSnapshotForVariantId(widget.dataset.variantId));
      });

      group.products.forEach((widget) => {
        const selectedVariantId = widget.dataset.selectedVariantId || parseVariantIds(widget.dataset.variantIds)[0] || '';
        updateProductWidget(widget, getSnapshotForVariantId(selectedVariantId));
      });
    }),
  );
}

function scheduleHydration() {
  window.clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(() => {
    hydrateInventory();
  }, 80);
}

function shouldRefreshForMutation(mutationList) {
  return mutationList.some((mutation) => {
    const changedNodes = [...mutation.addedNodes, ...mutation.removedNodes];

    return changedNodes.some((node) => {
      if (!(node instanceof HTMLElement)) {
        return false;
      }

      return (
        node.matches?.(CARD_SELECTOR) ||
        node.matches?.(PRODUCT_SELECTOR) ||
        Boolean(node.querySelector?.(CARD_SELECTOR)) ||
        Boolean(node.querySelector?.(PRODUCT_SELECTOR))
      );
    });
  });
}

function startObserver() {
  if (!(document.body instanceof HTMLElement) || domObserver) {
    return;
  }

  domObserver = new MutationObserver((mutationList) => {
    if (shouldRefreshForMutation(mutationList)) {
      scheduleHydration();
    }
  });

  domObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

function initTownwineInventory() {
  scheduleHydration();
  startObserver();
  document.addEventListener(ThemeEvents.FilterUpdate, scheduleHydration);
  window.addEventListener('pageshow', scheduleHydration);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTownwineInventory, { once: true });
} else {
  initTownwineInventory();
}
